/**
 * Config API – source of truth from backend (model_category_def, view_def).
 */
import { apiClient } from '../../apiHelper';
import { getCategoryMeasNames } from './measConfig';

export type { CategoryMeasDef, MeasValueType } from './measConfig';
export {
  buildCategoryIdToMeasLabel,
  buildCategoryIdToMeasNames,
  buildMeasDisplayNameMap,
  buildMeasGeometryContext,
  buildMeasLabelToCategoryId,
  buildPerimeterMeasLabels,
  buildViewMeasurementKeys,
  buildViewMeasurementKeysFromViewDef,
  categoryHasPairedLengthAndPerimeter,
  findCategoryByCode,
  findHcEllipseCategory,
  getCategoryMeasDefs,
  getCategoryMeasNames,
  getLengthMeasForCategory,
  getPairedBiometryCategoryIds,
  getPerimeterMeasForCategory,
  inferDefaultMeasForViewDraw,
} from './measConfig';
export type { MeasGeometryContext } from './measConfig';

export interface CategoryDef {
  id: number;
  code: string;
  display_name: string;
  category_group?: string | null;
  default_color_rgba?: string | null;
  default_tool?: string | null;
  /** All MEAS__* keys for this structure (text[] from DB) */
  meas_names?: string[] | null;
  meas_display_names?: string[] | null;
  meas_value_types?: string[] | null;
  max_structures?: number | null;
}

export interface QualityRuleResponse {
  category_id: number;
  rule_type: string;
}

export interface ViewDefResponse {
  id: number;
  view_code: string;
  display_name: string;
  quality_rules: QualityRuleResponse[];
}

function normalizeLabelForMeas(label: string): string {
  return (label ?? '').toLowerCase().trim().replace(/\s+/g, '_');
}

/** Build label/code -> primary meas_name from backend categories. */
export function buildLabelToMeasName(categories: CategoryDef[]): Record<string, string> {
  const out: Record<string, string> = {};
  categories.forEach((c) => {
    const meas = getCategoryMeasNames(c)[0];
    if (!meas) return;
    const codeKey = normalizeLabelForMeas(c.code);
    const displayKey = normalizeLabelForMeas(c.display_name ?? '');
    if (codeKey) out[codeKey] = meas;
    if (displayKey && displayKey !== codeKey) out[displayKey] = meas;
  });
  return out;
}

export function getMeasurementNameFromLabel(
  label: string | null | undefined,
  labelToMeasName: Record<string, string>
): string | null {
  if (!label || typeof label !== 'string') return null;
  return labelToMeasName[normalizeLabelForMeas(label)] ?? null;
}

export async function fetchCategories(): Promise<CategoryDef[]> {
  const response = await apiClient.get<unknown>('config/categories');
  const raw = response;
  if (Array.isArray(raw)) return raw as CategoryDef[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: CategoryDef[] }).data;
  }
  return [];
}

export async function fetchViews(): Promise<ViewDefResponse[]> {
  const response = await apiClient.get<ViewDefResponse[]>('config/views');
  return Array.isArray(response) ? response : (response as any) ?? [];
}

export function getMeasurementLabelForCategoryId(
  categoryId: number,
  categoryIdToMeasLabel: Record<number, string>
): string | null {
  return categoryIdToMeasLabel[categoryId] ?? null;
}

export function isMeasurementCategory(
  categoryId: number,
  categoryIdToMeasLabel: Record<number, string>
): boolean {
  return categoryId in categoryIdToMeasLabel;
}
